/**
 * A single media thumbnail — the cell `Gallery` repeats and `Lightbox` opens
 * full-screen. Renders an image (or a video with a decorative play badge),
 * an optional selected ring, and an optional loading skeleton. Presentation
 * only: the caller owns the media list and the click/keyboard result.
 */
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import { Icon } from './Icon.tsx';

export type MediaTileKind = 'image' | 'video';

const STYLE_ID = 'f4-media-tile';
const CSS = `
.f4-media-tile {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  padding: 0;
  overflow: hidden;
  background: var(--color-surface-sunken);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: box-shadow var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-media-tile[aria-pressed="true"] { box-shadow: 0 0 0 2px var(--color-accent) inset; }
.f4-media-tile__img { display: block; width: 100%; height: 100%; object-fit: cover; }
.f4-media-tile__badge {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  background: var(--color-scrim);
  opacity: 0;
  transition: opacity var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-media-tile--video .f4-media-tile__badge { opacity: 1; background: transparent; }
/* Geometry only — the shimmer itself is the shared \`.f4-skeleton\` utility. */
.f4-media-tile__skeleton { position: absolute; inset: 0; }
@media (prefers-reduced-motion: reduce) {
  .f4-media-tile, .f4-media-tile__badge { transition: none; }
}
`;

export interface MediaTileProps {
  /**
   * The asset URL. For an IMAGE this is the thumbnail; for a VIDEO it is the
   * video itself, rendered in a `<video>` rather than an `<img>`.
   *
   * It used to be contracted as "for video, a poster frame" while every caller
   * handed it the finished asset URL — so a generated video resolved as a broken
   * image in the thread. Fixed here rather than at the call sites because this is
   * the one component they all route through. A real poster FIELD is #81.
   */
  readonly src?: string;
  /** Accessible description of the media (required). */
  readonly alt: string;
  /** Media kind — a video shows a decorative play badge. Defaults to `image`. */
  readonly kind?: MediaTileKind;
  /** Marks the tile as the current selection (`aria-pressed`). */
  readonly selected?: boolean;
  /** Show a shimmering placeholder instead of the image. */
  readonly loading?: boolean;
  /** Roving-tabindex hook: only one tile in a `Gallery` is tab-stoppable. */
  readonly tabIndex?: number;
  /** Emitted on click/Enter/Space. */
  readonly onSelect?: () => void;
  /** Ref to the underlying `<button>` — lets `Gallery` drive roving focus. */
  readonly ref?: (el: HTMLButtonElement) => void;
  readonly id?: string;
  readonly class?: string;
}

export function MediaTile(props: MediaTileProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const kind = (): MediaTileKind => props.kind ?? 'image';

  return (
    <button
      type="button"
      id={props.id}
      ref={props.ref}
      class={cx(
        'f4-media-tile',
        'f4-focus-ring',
        kind() === 'video' && 'f4-media-tile--video',
        props.class,
      )}
      // The button's accessible name normally comes from the `<img>` alt. Two
      // cases have no img to borrow from, and each would otherwise leave an
      // unnamed focus stop: the loading skeleton (aria-hidden), and a video
      // (`<video>` takes no alt at all).
      aria-label={props.loading || kind() === 'video' ? props.alt : undefined}
      aria-pressed={props.selected ? 'true' : 'false'}
      tabIndex={props.tabIndex ?? 0}
      onClick={() => props.onSelect?.()}
    >
      <Show
        when={!props.loading}
        fallback={<span class="f4-media-tile__skeleton f4-skeleton" aria-hidden="true" />}
      >
        <Show
          when={kind() === 'video'}
          fallback={
            <img class="f4-media-tile__img" src={props.src} alt={props.alt} loading="lazy" />
          }
        >
          {/* `preload="metadata"` so the tile shows the first frame without
              pulling the whole file — a wall of tiles must not each fetch a
              video. No `controls`: the tile is itself a button that opens the
              Lightbox, and native controls inside it would swallow the click and
              add a second focus stop. `muted` + `playsinline` keep an autoplay
              a caller might add from hijacking the page or going fullscreen on
              iOS. */}
          <video
            class="f4-media-tile__img"
            src={props.src}
            preload="metadata"
            muted
            playsinline
            aria-hidden="true"
            tabIndex={-1}
          />
          <span class="f4-media-tile__badge" aria-hidden="true">
            <Icon name="play" size="lg" />
          </span>
        </Show>
      </Show>
    </button>
  );
}
